import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import logo from 'assets/logo.png'; import {
  TextField,
  Card,
  CardContent,
  CardActions,
  CardHeader,
  Button,
  Grid,
  Typography,
  AlertColor,
  Alert,
  Box,
  CardMedia
} from '@mui/material';

import { useSessionActions, useSessionUser, useErroLogin } from 'shared/store/session';
import { ILoginFormData } from 'shared/types';
import { InputError } from 'shared/components';
import { AMBIENTE_NOT_RELEASE } from 'config/env';

const validationSchema = yup.object({
  username: yup.string().required(),
  password: yup.string().required()
});


const Login = () => {

  const [isSubmitting, setIsSubmitting] = useState(false);
  const actions = useSessionActions();
  const erroLogin = useErroLogin();
  const user = useSessionUser();
  const [alert, setAlert] = useState<null | { text: string; type: AlertColor }>(null);
  const [msgErro] = useState({ username: '', password: '' });

  const { t } = useTranslation();

  const navigate = useNavigate();

  const onSubmit = async (data: ILoginFormData) => {

    setIsSubmitting(true);
    actions.login(data).then()
      .catch(() => {
        setAlert({
          text: t('messages.login.failed'),
          type: 'error',
        });
      })
      .finally(() => {
        setIsSubmitting(false);
      });
    actions.loginCrib(data).then()
      .catch(() => {
        setAlert({
          text: t('messages.login.failed'),
          type: 'error',
        });
      })
      .finally(() => {
        setIsSubmitting(false);
      });

  };

  React.useEffect(() => {
    if (erroLogin && !user) {
      if (erroLogin.codigo === 401) {
        setAlert({
          text: t('messages.login.failed'),
          type: 'error',
        });
      }
      else {
        setAlert({
          text: t('messages.login.error'),
          type: 'error',
        });

      }
    }
  }, [erroLogin]);

  React.useEffect(() => {
    if (user && !isSubmitting) {
      navigate('/');
    }
  }, [user, isSubmitting]);

  const { register, handleSubmit, formState: { errors }, } = useForm({
    resolver: yupResolver(validationSchema)
  });

  return (
    <Box component="form"
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        width: '450px',
        margin: '0 auto',
      }}
      noValidate autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
      <Card sx={{ marginTop: '80px' }}>
        <Grid container spacing={2}>
          <Grid container item xs={6}>
            <CardMedia
              component="img"
              height="60px"
              image={logo}
              alt="Logo"
              sx={{ textAlign: 'center', padding: '16px' }} />
          </Grid>
          <Grid container item xs={6}>
            <CardHeader sx={{ textAlign: 'center', padding: '16px', color: '#153085' }} title={t('global.namePortal')} />
          </Grid>
        </Grid>
        <CardContent>
          <div>
            {alert ? (
              <>
                <Grid item xs={12}>
                  <Alert severity={alert.type} onClose={() => setAlert(null)}>
                    {alert.text}
                  </Alert>
                </Grid>
              </>
            ) : (
              <Grid
                item
                xs={12}
                component={Typography}
              >
                {t('messages.login.initial')}
              </Grid>
            )}
            {AMBIENTE_NOT_RELEASE !== '' && (
              <Grid item xs={12}>
                <Alert severity={'warning'} >
                  {AMBIENTE_NOT_RELEASE}
                </Alert>
              </Grid>
            )}
            <TextField
              error={errors?.username?.type ? true : false}
              fullWidth
              id='username'
              type='text'
              label={`${t('pages.login.userName')} *`}
              disabled={isSubmitting}
              helperText={errors?.username?.type ? msgErro.username : ''}
              {...register('username')}
              sx={{ marginTop: '1rem', marginBottom: '0' }}
            />
            {errors?.username?.type && <InputError type={errors.username.type} field='username' />}
            <TextField
              error={errors?.password?.type ? true : false}
              fullWidth
              id='password'
              type='password'
              label={`${t('pages.login.password')} *`}
              disabled={isSubmitting}
              helperText={errors?.password?.type ? msgErro.password : ''}
              {...register('password')}
              sx={{ marginTop: '1rem', marginBottom: '0' }}
            />
            {errors?.password?.type && <InputError type={errors.password.type} field='password' />}
          </div>
        </CardContent>
        <CardActions sx={{ padding: '16px' }} >
          <Button
            variant="contained"
            type="submit"
            size="large"
            disabled={isSubmitting}
            color='primary'
            sx={{
              flexGrow: '1',
            }}
          >
            {!isSubmitting ? t('pages.login.login') : t('global.submitting')}
          </Button>
        </CardActions>
      </Card>
    </Box>
  );
};

export default Login;
